import { useEffect, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { startRinging } from "@/lib/chime";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace("https://", "wss://").replace("http://", "ws://");

const RING_MS = 30000;
const REMIND_EVERY_MS = 5 * 60 * 1000;

/** Pros only: 10 s chime + vibrate when a pro booking request arrives, then again every 5 min
 *  while any request is still unanswered (pending). Stops once accepted/declined or expired (server auto-cancels). */
export default function ProRequestAlert() {
  const { token, user } = useAuth();
  const seen = useRef(new Set());

  useEffect(() => {
    if (!token || user?.role !== "artisan" || !user?.id) return undefined;
    let ws = null;
    let retry = null;
    let closed = false;

    const alertFor = (bookingId) => {
      if (!bookingId || seen.current.has(bookingId)) return;
      seen.current.add(bookingId);
      startRinging({ untilMs: Date.now() + RING_MS, everyMs: 2500 });
    };

    const connect = () => {
      try {
        ws = new WebSocket(`${WS_BASE}/api/ws/${user.id}?token=${token}`);
        ws.onmessage = (e) => {
          try {
            const d = JSON.parse(e.data);
            if (d?.type === "new_booking") alertFor(d.booking_id);
            const t = String(d?.type || "");
            // Let the request slide-ups re-poll straight away instead of waiting for the next tick.
            if (t === "new_booking" || t.startsWith("rideshare_") || t.startsWith("offer_") || t.startsWith("booking_")) {
              window.dispatchEvent(new CustomEvent("yorm:booking-updated", { detail: { type: t, booking_id: d.booking_id, via: "ws" } }));
            }
          } catch (_) { /* ignore non-JSON */ }
        };
        ws.onclose = () => { if (!closed) retry = setTimeout(connect, 5000); };
      } catch (_) { retry = setTimeout(connect, 5000); }
    };
    connect();

    // Reminder: every 5 min, ring again while an unanswered pro request is still waiting.
    const remind = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/bookings?token=${token}&status=pending`);
        const waiting = (data || []).some((b) => b.booking_type !== "rideshare" && b.artisan_user_id === user.id);
        if (waiting) startRinging({ untilMs: Date.now() + RING_MS, everyMs: 2500 });
      } catch (_) { /* transient — try again next cycle */ }
    }, REMIND_EVERY_MS);

    // Foreground push (App.js re-broadcasts it) — same de-dupe by booking id.
    const onPush = (e) => {
      if (e?.detail?.type === "new_booking") alertFor(e.detail.booking_id);
    };
    window.addEventListener("yorm:booking-updated", onPush);

    return () => {
      closed = true;
      clearInterval(remind);
      if (retry) clearTimeout(retry);
      try { ws && ws.close(); } catch (_) { /* noop */ }
      window.removeEventListener("yorm:booking-updated", onPush);
    };
  }, [token, user?.role, user?.id]);

  return null;
}
